<template>
  <div id="add-other-form" v-if="webhookInitialized">
    <div class="copy-field">
      <span class="label">Webhook URL:</span>
      <span class="value">{{ webhookURL }}</span>
      <a-button @click="copyToClipboard(webhookURL, 'Webhook URL')">Copy</a-button>
    </div>

    <div class="copy-field">
      <span class="label">Webhook Key:</span>
      <span class="value">{{ webhookKey }}</span>
      <a-button @click="copyToClipboard(webhookKey, 'Webhook Key')">Copy</a-button>
    </div>

    <div id="instructions" v-if="!testData">
      <div class="label">Instructions</div>
      <div>A new URL has been generated (above) to connect with. Please take the following steps:</div>
      <div class="steps">
        <div>1. Copy the URL and paste it into the "Webhook URL" field on Google's system.</div>
        <div>2. Copy the Key and paste it into the "Key" field on Google's system.</div>
        <div>
          3. On Google's system click on "Send Test Data". This will confirm the connection and allow it to be
          activated.
        </div>
      </div>

      <div>
        Once the test data is received by our system, these instructions will disappear and you will be shown an editor
        to make changes to setup the mappings for this webhook.
      </div>

      <div class="waiting">
        <span>Waiting for test data from Google</span>
        <a-spin></a-spin>
      </div>
    </div>

    <a-form id="webhook-form" v-if="testData">
      <a-form-item label="Webhook Name" :label-col="{ span: 5 }" :wrapperCol="{ span: 13 }">
        <a-input placeholder="Webhook Name" v-model="webhookName" />
      </a-form-item>

      <a-form-item>
        <field-mappings :checking-form="savingForm" :is-production="isProduction"></field-mappings>
      </a-form-item>

      <a-button
        :disabled="webhookName == ''"
        :loading="savingForm"
        class="create-button"
        type="primary"
        @click="activateWebhook()"
        >{{ savingForm ? "Saving" : "Create Form Mapping" }}</a-button
      >
    </a-form>
    <!--</div>-->
  </div>

  <a-spin size="large" v-else></a-spin>
</template>

<script>
import axios from "axios";
import { mapState, mapMutations } from "vuex";
import AInput from "ant-design-vue/es/input/Input";
import VJsoneditor from "v-jsoneditor";
import FieldMappings from "@/components/FieldMappings";

export default {
  components: {
    AInput,
    VJsoneditor,
    FieldMappings,
  },
  computed: {
    ...mapState(["apiUrl", "user", "isProduction", "jsonData"]),
    webhookURL() {
      return `${this.apiUrl}/wh/${this.webhookId}`;
    },
  },
  data() {
    return {
      webhookId: null,
      webhookKey: null,
      webhookInitialized: false,
      webhookName: "",
      testData: false,
      // this is the timeout id for checking when the webhook has validated
      // which will enable killing off the retrying of the check if the user
      // navigates away from the page that shows this component.
      pollId: false,
      canSave: true,
      savingForm: false,
    };
  },
  methods: {
    ...mapMutations(["setJsonDataFromWebhook"]),
    copyToClipboard(text, type) {
      const listener = function(ev) {
        ev.preventDefault();
        ev.clipboardData.setData("text/plain", text);
      };
      document.addEventListener("copy", listener);
      document.execCommand("copy");
      document.removeEventListener("copy", listener);

      this.$message.success(`Copied ${type} to the clipboard`, 5);
    },

    activateWebhook() {
      this.savingForm = true;
      axios
        .request({
          method: "post",
          url: `${this.apiUrl}/manage-webhook`,
          headers: {
            Authorization: this.user.signInUserSession.idToken.jwtToken,
          },
          params: {
            id: this.webhookId,
            action: "ACTIVATE_WEBHOOK",
          },
          data: {
            name: this.webhookName,
            jsonData: this.jsonData,
            isProduction: this.isProduction,
          },
        })
        .then((rslt) => {
          alert("Webhook is now activated and can receive leads from Google.");
          this.savingForm = false;
          this.$router.push("/forms/google");
        })
        .catch((err) => alert(err));
    },
    // this is run over and over again every 5 seconds between completed calls to
    // check for test data from the webhook being sent by Google. The test call
    // with test data will confirm the connection between Google and the webhook
    // endpoint as well as communicate what the fields are going to be sent on
    // this webhook so we can setup mappings, or allow the user to setup mappings.
    checkForWebhookTestData() {
      // see if we've got any test data yet from Google
      axios
        .request({
          method: "get",
          url: `${this.apiUrl}/manage-webhook`,
          headers: {
            Authorization: this.user.signInUserSession.idToken.jwtToken,
          },
          params: {
            id: this.webhookId,
            action: "GET_WEBHOOK",
          },
        })
        .then((rslt) => {
          // just a little sanity check here: make sure that while waiting for the
          // response to come back from the server we didn't get lost in neverland
          // because the user navigated away from the page with this component on it.
          if (this.pollId) {
            if (rslt.data && rslt.data.data.TestData) {
              // ok we've got a response back with data
              // go ahead and set it and we're done polling
              this.testData = rslt.data.data.TestData;
              this.setJsonDataFromWebhook(this.testData);
              this.pollId = false;
            } else {
              // still nothing yet, keep polling
              this.pollId = setTimeout(this.checkForWebhookTestData, 5000);
            }
          }
        })
        .catch((err) =>
          alert(
            "Problem occurred waiting for the response from Google.\n\n" + err + "\n\nPlease start over and try again."
          )
        );
    },
  },
  mounted() {
    // if this component has loaded that means that we need to generate a new webhook
    // for the user to connect to Google. Get that from the server.
    axios
      .request({
        method: "post",
        url: `${this.apiUrl}/manage-webhook`,
        headers: {
          Authorization: this.user.signInUserSession.idToken.jwtToken,
        },
        params: {
          id: this.webhookId,
          action: "INIT_WEBHOOK",
        },
        data: {},
      })
      .then((rslt) => {
        // OK we've got the new webhook setup.
        this.webhookId = rslt.data.id;
        this.webhookKey = rslt.data.key;
        this.webhookInitialized = true;

        // Now we need to start polling to see if Google has sent over the
        // test data in a successful webhook post. Using a setTimeout here
        // rather than a setInterval because something could happen on the
        // network and it could take more than 5 seconds for the response
        // to come back and then you've potentially got two functions running
        // against separate calls to the server that could yield different
        // results from each other.
        this.pollId = setTimeout(this.checkForWebhookTestData, 5000);
      })
      .catch((err) => alert(err));
  },
  beforeDestroy: function() {
    // just as a safety measure, make sure if the user has been waiting for
    // a successful response and the background checking is still happening,
    // clear out that loop so it doesn't needlessly keep polling.
    clearTimeout(this.pollId);
    this.pollId = false;
  },
};
</script>

<style lang="scss" scoped>
.label {
  font-size: 1.2em;
  font-weight: 700;
}

.copy-field {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;

  span {
    margin: 0 10px 0 0;
  }

  .label {
    width: 15%;
  }

  .value {
    border: 1px solid gray;
    border-radius: 5px;
    padding: 8px;
    width: 60%;
  }
}

#instructions {
  margin: 30px auto 0;
  width: 75%;

  .label {
    text-align: center;
    margin-bottom: 20px;
  }
  .steps {
    margin: 20px 15px;
  }
  .waiting {
    margin-top: 30px;
    display: flex;
    justify-content: center;

    span {
      margin-right: 10px;
      font-weight: 700;
      font-size: 1.3em;
    }
  }
}

#webhook-form {
  margin-top: 50px;
  display: flex;
  flex-direction: column;
}

.create-button {
  margin: 25px auto;
  width: 200px;
}
</style>
